/** @prettier */

import {useState, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import type {LoaderFunctionArgs} from 'react-router-dom';
import {Grid, Card, CardContent, Box, Stack} from '@mui/material';
import {useLoaderData, useParams, Link, useLocation} from 'react-router-dom';

//MTH component import
import RTAddDialog from './RTAddDialog';
import RTEditDialog from './RTEditDialog';
import RTDeleteDialog from './RTDeleteDialog';
import RTAdvancedDialog from './RTAdvancedDialog';
import CardContents from '../../component/CardContents';
import GearMenu, {GearMenuListArgType} from '../../component/GearMenuList';
import GWConfigUpdateDrawer from '../gw-registration/GWConfigUpdateDrawer';
//API import
import {
  getCompositionsGateway,
  getCompositionsRTUnit,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {iconBlack} from '../../define';
import {useOutlet} from '../gw-registration/GWMain';
import {gwInfoSort, sortByUnicode} from '../../functions/sort';
import {checkUnitExistenceByGatewayID} from '../../functions/checkUnitExistence';

type loaderReturn = {
  /** サイトに登録されているゲートウェイの情報 */
  allGWInfo: apiIF.res.getCompositionsGateway;
  /** 表示しているゲートウェイが課金しているかどうか */
  isBilling: boolean;
  /** 表示しているゲートウェイに登録されている熱源機の情報 */
  rtInfo: apiIF.res.getCompositionsRTUnit;
  /** 登録ユニットの有無 */
  hasUnit: boolean;
};
export async function loader({
  params,
}: LoaderFunctionArgs): Promise<loaderReturn | undefined> {
  //パスパラメータを取得できない場合
  if (params.siteID === undefined || params.gwID === undefined) {
    return undefined;
  }

  try {
    const promises = [];
    //ゲートウェイの情報を取得
    promises.push(getCompositionsGateway(params.siteID, {'max-result': 100}));
    //熱源機の情報を取得
    promises.push(
      getCompositionsRTUnit(params.siteID, params.gwID, {'max-result': 400})
    );
    // ゲートウェイの機器登録状況を取得
    promises.push(checkUnitExistenceByGatewayID(params.siteID, params.gwID));
    const response = await Promise.all(promises);

    const allGWInfo = response[0] as apiIF.res.getCompositionsGateway;
    const rtInfo = response[1] as apiIF.res.getCompositionsRTUnit;
    /** ユニットの登録状況 */
    const unitExistence = response[2] as boolean;

    //課金状況の判定
    let isBilling = false;
    const displayGWInfo = allGWInfo['gateway-informations'].find(gwInfo => {
      return gwInfo['gateway-id'] === params.gwID;
    });
    if (
      displayGWInfo !== undefined &&
      displayGWInfo['billing-status'] !== undefined
    ) {
      isBilling = displayGWInfo['billing-status'] !== 'unpaid';
    }

    //表示内容をUnicode順に並び替え
    rtInfo['unit-informations'].sort(sortByUnicode('unit-name'));
    return {
      allGWInfo: gwInfoSort(allGWInfo),
      isBilling: isBilling,
      rtInfo: rtInfo,
      hasUnit: unitExistence as boolean,
    };
  } catch {
    return undefined;
  }
}

/**
 * 熱源機一覧
 * @returns JSX.Element
 */
const RTRegistration = () => {
  const trans = useTranslation().t; //翻訳関数
  const loaderData = useLoaderData() as loaderReturn | undefined;
  //URL取得
  const location = useLocation();
  //パスパラメータ取得
  const {siteID, gwID, systemType: rtType} = useParams();
  //親コンポーネントから値を取得
  const {setAllGWInfo} = useOutlet();

  const [isBilling, setIsBilling] = useState(false); //表示しているゲートウェイが課金しているかどうか
  //prettier-ignore
  const [rtInfo, setRTInfo] = useState<apiIF.res.getCompositionsRTUnit | null>(null); //熱源機の情報
  const [isOpenEditDialog, setIsOpenEditDialog] = useState(false); //編集ダイアログ表示フラグ
  const [isOpenAdvancedDialog, setIsOpenAdvancedDialog] = useState(false); //拡張設定ダイアログ表示フラグ
  const [isOpenDeleteDialog, setIsOpenDeleteDialog] = useState(false); //削除ダイアログ表示フラグ
  const [selectedUnitID, setSelectedUnitID] = useState(''); //選択した熱源機のID
  const [hasConfigGW, setHasConfigGW] = useState<boolean>(false); //GW毎の構成情報未反映の有無
  const [hasUnit, setHasUnit] = useState<boolean>(false); //GWの機器登録有無

  /** 熱源機追加ボタンの名前 */
  const addButtonName = trans('RTRegistration.buttonTxt.addUnit');
  /** RTタイプ変換テーブル */
  const rtTypeTable: {[key: string]: string} = {
    rt1: trans('RTRegistration.title.remoteTerminal1'),
    rt2: trans('RTRegistration.title.remoteTerminal2'),
  };
  /** 電源の種類変換テーブル */
  const powerSupplyTable: {[value: string]: string} = {
    threePhase: trans('RTRegistration.item.value.powerSupply.threePhase'),
    singlePhase: trans('RTRegistration.item.value.powerSupply.singlePhase'),
  };
  /** 熱源機タイプ変換テーブル */
  const unitTypeTable: {[value: string]: string} = {
    QtonOpen: trans('RTRegistration.item.value.unitType.qtonOpen'),
    QtonClose: trans('RTRegistration.item.value.unitType.qtonClose'),
    Hyozan: trans('RTRegistration.item.value.unitType.hyozan'),
    Chiller: trans('RTRegistration.item.value.unitType.chiller'),
  };

  useEffect(() => {
    if (loaderData !== undefined) {
      setAllGWInfo(loaderData.allGWInfo);
      setIsBilling(loaderData.isBilling);
      setRTInfo(loaderData.rtInfo);
      setHasUnit(loaderData.hasUnit);

      //構成情報未反映の有無をチェック
      for (const gwInfo of loaderData.allGWInfo['gateway-informations']) {
        if (gwInfo['gateway-id'] !== gwID) continue;
        setHasConfigGW(
          loaderData.isBilling &&
            gwInfo['composition-receipt-version'] !== undefined &&
            gwInfo['composition-receipt-version'] !== 'latest'
        );
        break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaderData]);

  //熱源機の情報が更新された場合、ツリービューを更新
  useEffect(() => {
    //パスパラメータを取得できない場合
    if (siteID === undefined) {
      return;
    }

    //ゲートウェイの情報を取得
    getCompositionsGateway(siteID, {'max-result': 100}).then(response => {
      //表示内容をUnicode順に並び替え
      response = gwInfoSort(response);
      setAllGWInfo(response);
      //ユニットの有無をチェック
      for (const gwInfo of response['gateway-informations']) {
        if (gwInfo['gateway-id'] !== gwID) continue;
        checkUnitExistenceByGatewayID(siteID, gwID).then(response => {
          setHasUnit(response as boolean);
        });
        break;
      }
    });
  }, [rtInfo, siteID]);

  /**
   * Editボタン押下時処理
   * @param unitID 熱源機ID
   */
  const openEditDialog = (unitID: string) => {
    setIsOpenEditDialog(true);
    setSelectedUnitID(unitID);
  };
  /**
   * Advanced Settingボタン押下時処理
   * @param unitID 熱源機ID
   */
  const openAdvancedSettingDialog = (unitID: string) => {
    setIsOpenAdvancedDialog(true);
    setSelectedUnitID(unitID);
  };
  /**
   * Deleteボタン押下時処理
   * @param unitID 熱源機ID
   */
  const openDeleteDialog = (unitID: string) => {
    setIsOpenDeleteDialog(true);
    setSelectedUnitID(unitID);
  };

  /**
   * 登録されている熱源機の台数をカウント
   * @returns 台数
   */
  const unitNumCount = () => {
    //パスパラメータを取得できない場合
    //熱源機の情報を取得できない場合
    if (rtType === undefined || rtInfo === null) {
      return 0;
    }

    return rtInfo['unit-informations'].reduce((result, unitInfo) => {
      //表示している系統番号のみカウント
      if (unitInfo['system'] === rtType.slice(2)) {
        result++;
      }
      return result;
    }, 0);
  };

  /**
   * 親熱源機の名前を取得
   * @param parentID 親熱源機ID
   * @returns 親熱源機の名前
   */
  const getParentUnitName = (parentID: string) => {
    if (rtInfo === null) {
      return trans('RTRegistration.item.value.parentUnit.none');
    }

    for (const unitInfo of rtInfo['unit-informations']) {
      if (
        unitInfo['unit-id'] === parentID &&
        unitInfo['unit-name'] !== undefined
      ) {
        return unitInfo['unit-name'];
      }
    }
    return trans('RTRegistration.item.value.parentUnit.none');
  };

  /**
   * 歯車メニューリスト
   * @param unitID 熱源機ID
   * @returns メニュー一覧
   */
  const gearMenuItem = (
    unitID: string,
    unitType: apiIF.resParameters.rtUnitInformations['unit-type']
  ): GearMenuListArgType['menuItem'] => {
    let gearMenuItemList = [];

    // pushした順に上から表示されるため順番は変えないこと
    // 編集ダイアログ表示ボタン
    gearMenuItemList.push({
      label: trans('RTRegistration.menuItem.edit'),
      onClick: () => openEditDialog(unitID),
      icon: {
        className: 'gwSetting__contents__button__icon',
        src: iconBlack.EDIT.src,
        alt: trans(
          `RTRegistration.image.alternativeText.${iconBlack.EDIT.alt}`
        ),
      },
    });
    // 拡張設定ダイアログ表示ボタン（Q-tonのみ）
    if (
      unitType !== undefined &&
      (unitType === 'QtonClose' || unitType === 'QtonOpen')
    ) {
      gearMenuItemList.push({
        label: trans('RTRegistration.menuItem.advanced'),
        onClick: () => openAdvancedSettingDialog(unitID),
        icon: {
          className: 'gwSetting__contents__button__icon',
          src: iconBlack.SETTINGS.src,
          alt: trans(
            `RTRegistration.image.alternativeText.${iconBlack.EDIT.alt}`
          ),
        },
      });
    }
    // 削除確認ダイアログ表示ボタン
    gearMenuItemList.push({
      label: trans('RTRegistration.menuItem.delete'),
      onClick: () => openDeleteDialog(unitID),
      icon: {
        className: 'gwSetting__contents__button__icon',
        src: iconBlack.DELETE.src,
        alt: trans(
          `RTRegistration.image.alternativeText.${iconBlack.DELETE.alt}`
        ),
      },
    });

    return gearMenuItemList;
  };

  //熱源機の情報を取得できない場合
  //パスパラメータを取得できない場合
  if (rtInfo === null || rtType === undefined) {
    return <></>;
  }
  return (
    <>
      <Box className="gwSetting__title">{rtTypeTable[rtType]}</Box>
      <Stack spacing={4}>
        {/* 熱源機追加ボタン */}
        <Box className="gwSetting__contents--unit gwSetting__contents__button--add">
          <RTAddDialog
            addButtonName={addButtonName}
            rtInfo={rtInfo}
            setRTInfo={setRTInfo}
            isBilling={isBilling}
            setHasConfigGW={setHasConfigGW}
          />
        </Box>
        {/* 登録済熱源機が無い場合は、熱源機追加ボタンのみ表示 */}
        {unitNumCount() === 0 ? (
          <Card className="gwSetting__contents--unit">
            <CardContent className="gwSetting__contents__noRegister">
              {trans('RTRegistration.instruction', {name: addButtonName})}
            </CardContent>
          </Card>
        ) : (
          //登録済み熱源機
          rtInfo['unit-informations']
            //一致するRT系統で検索
            .filter(unitInfo => unitInfo['system'] === rtType.slice(2))
            .map(unitInfo => (
              <Card
                key={unitInfo['unit-id']}
                className="gwSetting__contents--unit">
                <CardContent>
                  <Stack spacing={2}>
                    {/* 熱源機名称と歯車 */}
                    <Grid
                      container
                      justifyContent="space-between"
                      className="gwSetting__contents__title">
                      {/* 熱源機名称 */}
                      <Grid item xs="auto">
                        {unitInfo['unit-type'] === 'Hyozan' ? (
                          <Link
                            to={`${location.pathname}/${unitInfo['unit-id']}/rt-iu-registration${location.search}`}>
                            {unitInfo['unit-name']}
                          </Link>
                        ) : (
                          unitInfo['unit-name']
                        )}
                      </Grid>
                      {/* 歯車ボタン */}
                      <Grid item xs={1}>
                        <GearMenu
                          id={`mv-btnRTSettingOpen${unitInfo['unit-id']}`}
                          menuItem={gearMenuItem(
                            unitInfo['unit-id'],
                            unitInfo['unit-type']
                          )}
                        />
                        {/* 選択した熱源機のダイアログのみ出す */}
                        {unitInfo['unit-id'] === selectedUnitID &&
                        unitInfo['unit-name'] !== undefined ? (
                          <>
                            <RTEditDialog
                              unitID={unitInfo['unit-id']}
                              displayRTInfo={unitInfo}
                              setRTInfo={setRTInfo}
                              isOpenDialog={isOpenEditDialog}
                              setIsOpenDialog={setIsOpenEditDialog}
                              isBilling={isBilling}
                              setHasConfigGW={setHasConfigGW}
                            />
                            <RTAdvancedDialog
                              unitID={unitInfo['unit-id']}
                              displayRTInfo={unitInfo}
                              setRTInfo={setRTInfo}
                              isOpenDialog={isOpenAdvancedDialog}
                              setIsOpenDialog={setIsOpenAdvancedDialog}
                              isBilling={isBilling}
                              setHasConfigGW={setHasConfigGW}
                            />
                            <RTDeleteDialog
                              unitID={unitInfo['unit-id']}
                              unitName={unitInfo['unit-name']}
                              setRTInfo={setRTInfo}
                              isOpenDialog={isOpenDeleteDialog}
                              setIsOpenDialog={setIsOpenDeleteDialog}
                              isBilling={isBilling}
                              setHasConfigGW={setHasConfigGW}
                            />
                          </>
                        ) : (
                          <></>
                        )}
                      </Grid>
                    </Grid>
                    {/* 熱源機の情報 */}
                    <Stack
                      spacing={2}
                      className="gwSetting__contents__contents">
                      {/* RTアドレス */}
                      <CardContents
                        class="gwSetting__contents__contents__underLine"
                        valueDescription={trans(
                          'RTRegistration.item.title.remoteTerminalAddress'
                        )}
                        value={
                          unitInfo['address'] === undefined
                            ? ''
                            : unitInfo['address']
                        }
                      />
                      {/* 入力電圧 */}
                      <CardContents
                        class="gwSetting__contents__contents__underLine"
                        valueDescription={trans(
                          'RTRegistration.item.title.voltage'
                        )}
                        value={
                          unitInfo['input-voltage'] === undefined
                            ? ''
                            : unitInfo['input-voltage']
                        }
                      />
                      {/* 電源の種類 */}
                      <CardContents
                        class="gwSetting__contents__contents__underLine"
                        valueDescription={trans(
                          'RTRegistration.item.title.powerSupply'
                        )}
                        value={
                          unitInfo['power-supply'] === undefined
                            ? ''
                            : powerSupplyTable[unitInfo['power-supply']]
                        }
                      />
                      {/* 熱源機タイプ */}
                      <CardContents
                        class="gwSetting__contents__contents__underLine"
                        valueDescription={trans(
                          'RTRegistration.item.title.unitType'
                        )}
                        value={
                          unitInfo['unit-type'] === undefined
                            ? ''
                            : unitTypeTable[unitInfo['unit-type']]
                        }
                      />
                      {/* 版銘 */}
                      <CardContents
                        class="gwSetting__contents__contents__underLine"
                        valueDescription={trans(
                          'RTRegistration.item.title.nameplate'
                        )}
                        value={
                          unitInfo['nameplate'] === undefined
                            ? ''
                            : unitInfo['nameplate']
                        }
                      />
                      {/* ソフトバージョン */}
                      {unitInfo['advanced-setting'] === 'active' ? (
                        <CardContents
                          class="gwSetting__contents__contents__underLine"
                          valueDescription={trans(
                            'RTRegistration.item.title.model'
                          )}
                          value={
                            unitInfo['unit-model'] === undefined
                              ? ''
                              : `${trans(
                                  `RTRegistration.item.value.model.${unitInfo[
                                    'unit-model'
                                  ].toLowerCase()}`
                                )}`
                          }
                        />
                      ) : (
                        <></>
                      )}
                      {/* ポンプモデル */}
                      {unitInfo['advanced-setting'] === 'active' ? (
                        <CardContents
                          class="gwSetting__contents__contents__underLine"
                          valueDescription={trans(
                            'RTRegistration.item.title.pumpModel'
                          )}
                          value={
                            unitInfo['pump-model'] === undefined
                              ? ''
                              : unitInfo['pump-model']
                          }
                        />
                      ) : (
                        <></>
                      )}
                      {/* 親熱源機 */}
                      {unitInfo['unit-type'] === 'QtonOpen' ||
                      unitInfo['unit-type'] === 'QtonClose' ? (
                        <CardContents
                          class="gwSetting__contents__contents__underLine"
                          valueDescription={trans(
                            'RTRegistration.item.title.parentUnit'
                          )}
                          value={
                            unitInfo['parent-unit'] === undefined
                              ? `${trans(
                                  'RTRegistration.item.value.parentUnit.none'
                                )}`
                              : getParentUnitName(unitInfo['parent-unit'])
                          }
                        />
                      ) : (
                        <></>
                      )}
                      {/* 接続室内機 */}
                      {unitInfo['unit-type'] === 'Hyozan' ? (
                        <CardContents
                          class="gwSetting__contents__contents__underLine"
                          valueDescription={trans(
                            'RTRegistration.item.title.indoorUnit'
                          )}
                          value={
                            unitInfo['indoor-unit-informations'] ===
                            undefined ? (
                              <></>
                            ) : (
                              <Stack spacing={0}>
                                {unitInfo['indoor-unit-informations'].map(
                                  iuInfo => (
                                    <Box key={iuInfo['unit-id']}>
                                      {iuInfo['indoor-unit-name']}
                                    </Box>
                                  )
                                )}
                              </Stack>
                            )
                          }
                        />
                      ) : (
                        <></>
                      )}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            ))
        )}
      </Stack>
      {/* 構成情報反映ボタン */}
      <GWConfigUpdateDrawer
        hasConfigGW={hasConfigGW}
        setHasConfigGW={setHasConfigGW}
        hasUnit={hasUnit}
      />
    </>
  );
};
export default RTRegistration;
